import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import PaginationComponent from '../../shared/components/PaginationComponent';
import PreleadQualifyConfirmModal from '../components/ui/PreleadQualifyConfirmModal';
import PreleadLogModal, { PreleadNoteButton } from '../components/ui/PreleadLogModal';
import { createLead, getPreLeads, markPreLeadAsUnqualified } from '../services/api/implementation/impl';
import { capitalizeWords, formatStatusLabel } from '../utils/stringUtils';

function formatCellDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function normalizePreleadState(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function getRelevantCampaignSend(sends, preleadState) {
  if (!Array.isArray(sends) || sends.length === 0) return null;
  const normalizedState = normalizePreleadState(preleadState);
  const byLatestSentAt = (a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0);
  const latestSend = [...sends].sort(byLatestSentAt)[0];
  const interestedSend = sends
    .filter((send) => normalizePreleadState(send?.state) === 'interested')
    .sort(byLatestSentAt)[0];
  const notInterestedSend = sends
    .filter((send) => normalizePreleadState(send?.state) === 'not_interested')
    .sort(byLatestSentAt)[0];

  if (normalizedState === 'interested') return interestedSend ?? latestSend;
  if (normalizedState === 'not_interested') return notInterestedSend ?? latestSend;
  return latestSend;
}

function getProjectNameFromProjectObject(prelead) {
  return prelead?.interested_campaign?.project?.name?.trim() || '—';
}

function getAnswerLabel(answer) {
  if (!answer) return '—';
  return (
    answer?.questionOption?.label ||
    answer?.question_option?.label ||
    answer?.raw_text ||
    answer?.raw_payload ||
    '—'
  );
}

function getQuestionOptionLabels(answer) {
  const optionBuckets = [
    answer?.question?.options,
    answer?.question?.questionOptions,
    answer?.question?.question_options,
    answer?.question_options,
    answer?.options
  ];

  const flattenedOptions = optionBuckets.flatMap((bucket) => (Array.isArray(bucket) ? bucket : []));
  const labels = flattenedOptions
    .map((option) =>
      String(
        option?.label ?? option?.option_label ?? option?.text ?? option?.title ?? option?.value ?? ''
      ).trim()
    )
    .filter(Boolean);

  return Array.from(new Set(labels));
}

function WhatsAppFlowAnswersModal({ selectedPrelead, onClose }) {
  const answers = selectedPrelead?.whatsapp_flow_question_answers ?? [];

  return (
    <Modal width={50} name="WhatsApp Flow Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {answers.length > 0 ? (
          answers
            .slice()
            .sort((a, b) => (a?.question?.question_order ?? 999) - (b?.question?.question_order ?? 999))
            .map((answer, index) => {
              const selectedAnswer = getAnswerLabel(answer);
              const selectedAnswerNorm = selectedAnswer.toLowerCase().trim();
              const otherOptions = getQuestionOptionLabels(answer).filter(
                (label) => label.toLowerCase().trim() !== selectedAnswerNorm
              );

              return (
                <div
                  key={answer.id ?? `${answer.question_id}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Question {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">{answer?.question?.prompt_text || '—'}</p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Selected Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900">{selectedAnswer}</p>
                  </div>

                  <div className="mt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-customBlue">Other Options</p>
                    {otherOptions.length > 0 ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {otherOptions.map((optionLabel) => (
                          <span
                            key={optionLabel}
                            className="inline-flex rounded-full border border-customBlue/20 bg-customBlue/5 px-2.5 py-1 text-xs text-customBlue">
                            {optionLabel}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">No other options captured.</p>
                    )}
                  </div>
                </div>
              );
            })
        ) : (
          <p className="text-sm text-gray-500">No WhatsApp flow answers available.</p>
        )}
      </div>
    </Modal>
  );
}

const FILTER_CONFIG = {
  all: {
    title: 'All Preleads',
    query: { limit: 10 }
  },
  qualified: {
    title: 'Qualified Preleads',
    query: { limit: 10, converted_to_lead: true }
  },
  'potential-qualified': {
    title: 'Potential Qualified Preleads',
    query: { limit: 10, state: 'interested' }
  },
  'not-interested': {
    title: 'Not Interested Preleads',
    query: { limit: 10, state: 'not_interested' }
  },
  awaiting: {
    title: 'Awaiting Response Preleads',
    query: { limit: 10, state: 'pending' }
  }
};

const AllPreleadLeadsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { collapsed } = useSidebar();
  const isMarketing = location.pathname.startsWith('/builder/marketing');
  const [page, setPage] = useState(1);
  const [pendingQualify, setPendingQualify] = useState(null);
  const [selectedPreleadForAnswers, setSelectedPreleadForAnswers] = useState(null);
  const [selectedPreleadForLogs, setSelectedPreleadForLogs] = useState(null);
  const filterRaw = searchParams.get('filter') || 'all';
  const filter =
    filterRaw === 'disqualified' || filterRaw === 'unqualified' ? 'not-interested' : filterRaw;
  const selectedConfig = FILTER_CONFIG[filter] || FILTER_CONFIG.all;
  const queryPayload = selectedConfig.query;

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['allPreLeads', filter, page],
    queryFn: () => getPreLeads({ page, ...queryPayload }),
    keepPreviousData: true
  });

  const preleads = data?.preLeads ?? [];
  const paginationMeta = data?.meta ?? { page: 1, totalPages: 1 };

  const rows = useMemo(
    () =>
      preleads.map((p) => ({
        id: p.id,
        pre_lead_id: p.id,
        name: p.name ?? '—',
        countryCode: p.countryCode ?? '',
        phone: p.phone ?? '',
        phone_display: [p.countryCode, p.phone].filter(Boolean).join(' ').trim() || '—',
        email: p.email ?? '—',
        state: p.state ?? '—',
        alternate_phone_number: p.alternate_phone_number ?? '—',
        min_estimated_budget: p.min_estimated_budget ?? null,
        max_estimated_budget: p.max_estimated_budget ?? null,
        occupation: p.occupation ?? null,
        reference_name: p.reference_name ?? null,
        type: p.type ?? null,
        expected_budget: p.expected_budget ?? null,
        expected_closure_date: p.expected_closure_date ?? null,
        accommodations: p.accommodations ?? [],
        project_accommodations: p.project_accommodations ?? [],
        plotArea: p.plotArea ?? p.plot_area ?? [],
        address: p.address ?? null,
        gender: p.gender ?? null,
        source: p.source ?? null,
        is_disqualified: p.is_disqualified ?? false,
        lead_id: p.lead?.id ?? null,
        whatsapp_campaign_sends: p.whatsapp_campaign_sends ?? [],
        whatsapp_flow_question_answers: p.whatsapp_flow_question_answers ?? [],
        project_name: getProjectNameFromProjectObject(p),
        reason: p.reason ?? null,
        log: p.log ?? null,
        created_at: formatCellDate(p.created_at)
      })),
    [preleads]
  );

  const createPreleadLeadMutation = useMutation({
    mutationFn: (payload) => createLead(payload)
  });
  const unqualifyPreleadMutation = useMutation({
    mutationFn: ({ preLeadId, reason }) => markPreLeadAsUnqualified(preLeadId, reason)
  });

  const refreshCurrentListing = () => {
    queryClient.invalidateQueries({ queryKey: ['allPreLeads'] });
  };

  const handleConfirmQualify = async (row) => {
    if (!row) return;
    if (row.lead_id) {
      navigate(`/builder/sales/${row.lead_id}`);
      return;
    }

    const interestedCampaignSend = row.whatsapp_campaign_sends
      ?.filter((send) => String(send?.state ?? '').trim().toLowerCase() === 'interested')
      .sort((a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0))[0];
    const projectId = Number(interestedCampaignSend?.WhatsAppCampaign?.project_id);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      toast.error('No interested campaign project found for this prelead.');
      return;
    }

    const mapToIdArray = (items) =>
      Array.isArray(items) ? items.map((item) => item?.id ?? item).filter(Boolean) : [];

    const payload = {
      id: row.id,
      pre_lead_id: row.pre_lead_id ?? row.id,
      name: row.name || `Prelead ${row.id}`,
      countryCode: row.countryCode,
      phone_number: row.phone,
      source: row.source || 'whatsapp_outbound',
      interested_projects: [projectId]
    };

    if (row.email && row.email !== '—') payload.email = row.email;
    if (row.alternate_phone_number && row.alternate_phone_number !== '—') {
      payload.alternate_phone_number = row.alternate_phone_number;
    }
    if (row.occupation) payload.occupation = row.occupation;
    if (row.reference_name) payload.reference_name = row.reference_name;
    if (row.address) payload.address = row.address;
    if (row.gender) payload.gender = row.gender;
    if (row.type) payload.type = row.type;
    if (row.expected_closure_date) payload.expected_clouser_date = row.expected_closure_date;

    const interestedAccommodationType = mapToIdArray(row.accommodations);
    if (interestedAccommodationType.length) {
      payload.interestedAccomodationType = interestedAccommodationType;
    }

    const projectAccommodationIds = mapToIdArray(row.project_accommodations);
    if (projectAccommodationIds.length) {
      payload.accomodations = projectAccommodationIds;
    }

    const plotAreaIds = mapToIdArray(row.plotArea);
    if (plotAreaIds.length) {
      payload.plotArea = plotAreaIds;
    }

    if (row.min_estimated_budget != null && row.max_estimated_budget != null) {
      payload.expectedBudgetRange = `${row.min_estimated_budget}-${row.max_estimated_budget}`;
    } else if (row.expected_budget != null && Number(row.expected_budget) > 0) {
      payload.expectedBudgetRange = String(row.expected_budget);
    }

    try {
      const result = await createPreleadLeadMutation.mutateAsync(payload);
      const leadId = result?.id ?? result?.data?.id;
      toast.success('Lead created successfully.');
      refreshCurrentListing();
      if (leadId) {
        navigate(`/builder/sales/${leadId}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not create lead.');
    }
  };

  const handleConfirmUnqualify = async (row, reason) => {
    if (!row?.id) return;
    try {
      await unqualifyPreleadMutation.mutateAsync({ preLeadId: row.id, reason });
      toast.success('Marked as Unqualified.');
      refreshCurrentListing();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not update prelead.');
    }
  };

  const goBack = () => {
    if (isMarketing) {
      navigate('/builder/marketing/prelead-lists');
      return;
    }
    try {
      localStorage.setItem('salesMainSecondaryTab', 'Prelead List');
    } catch {
      // ignore localStorage failure
    }
    navigate('/builder/sales/', { state: { secondaryTab: 'Prelead List' } });
  };

  return (
    <Layout>
      <Loader isVisible={isLoading} />
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow
          onBack={goBack}
          data={{ name: `${isMarketing ? 'Marketing' : 'Sales'} > Prelead lists > All Leads` }}
        />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] min-h-[calc(100vh-5rem)] bg-gray-50 px-2 pb-10 pt-4 sm:px-4 lg:px-6">
          <div className="mx-auto max-w-screen-2xl space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <h2 className="text-base font-semibold text-customBlue">{selectedConfig.title}</h2>
            </div>

            {isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error?.response?.data?.message || error?.message || 'Could not load preleads.'}
              </div>
            )}

            {!isLoading && !isError && (
              <div className="min-h-[24rem] overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-[#E9F5F6] text-customBlue">
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Name
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Phone
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Email
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Status
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Project Name
                      </th>
                      {!isMarketing && (
                        <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                          Flow Response
                        </th>
                      )}
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Created
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Notes
                      </th>
                      {!isMarketing && (
                        <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap border-b border-customBlue/15">
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/80">
                        <td className="px-3 py-2 text-gray-800">
                          {row.name && row.name !== '—' ? capitalizeWords(String(row.name)) : row.name}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{row.phone_display}</td>
                        <td className="px-3 py-2 text-gray-800">{row.email}</td>
                        <td className="px-3 py-2 text-gray-800">
                          {row.state && row.state !== '—'
                            ? formatStatusLabel(String(row.state))
                            : row.state}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{row.project_name}</td>
                        {!isMarketing && (
                          <td className="px-3 py-2">
                            {Array.isArray(row.whatsapp_flow_question_answers) &&
                            row.whatsapp_flow_question_answers.length > 0 ? (
                              <button
                                type="button"
                                onClick={() => setSelectedPreleadForAnswers(row)}
                                className="inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors">
                                View Q&A
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2 text-gray-800">{row.created_at}</td>
                        <td className="px-3 py-2">
                          <PreleadNoteButton
                            row={row}
                            onClick={() => setSelectedPreleadForLogs(row)}
                          />
                        </td>
                        {!isMarketing && (
                          <td className="px-3 py-2 text-center">
                            {(() => {
                              const currentState = String(row.state ?? '').toLowerCase();
                              const isInterested = currentState === 'interested';
                              if (!isInterested) return '—';
                              if (row.lead_id != null) {
                                return <div className="text-green-700 font-semibold">Qualified</div>;
                              }
                              if (row.is_disqualified) {
                                return <div className="text-red-700 font-semibold">Unqualified</div>;
                              }
                              return (
                                <div className="flex flex-wrap items-center justify-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => setPendingQualify({ kind: 'qualified', row })}
                                    className="inline-flex items-center rounded-lg border border-green-600/80 bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-800 hover:bg-green-100 transition-colors">
                                    Qualified
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPendingQualify({ kind: 'unqualified', row })}
                                    className="inline-flex items-center rounded-lg border border-red-600/80 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-800 hover:bg-red-100 transition-colors">
                                    Unqualified
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                        )}
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={isMarketing ? 7 : 9} className="px-3 py-8 text-center text-sm text-gray-500">
                          No preleads found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && !isError && paginationMeta.totalPages > 1 && (
              <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <PaginationComponent
                  page={page}
                  totalPages={paginationMeta.totalPages}
                  onPageChange={(nextPage) => {
                    const boundedPage = Math.max(1, Math.min(paginationMeta.totalPages, Number(nextPage)));
                    setPage(boundedPage);
                  }}
                  isFetching={isFetching}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <PreleadQualifyConfirmModal
        open={Boolean(pendingQualify)}
        kind={pendingQualify?.kind ?? 'qualified'}
        isSubmitting={createPreleadLeadMutation.isPending || unqualifyPreleadMutation.isPending}
        onClose={() => {
          if (createPreleadLeadMutation.isPending || unqualifyPreleadMutation.isPending) return;
          setPendingQualify(null);
        }}
        onConfirm={async (reason) => {
          if (!pendingQualify) return;
          if (pendingQualify.kind === 'qualified') {
            await handleConfirmQualify(pendingQualify.row);
          } else {
            await handleConfirmUnqualify(pendingQualify.row, reason);
          }
          setPendingQualify(null);
        }}
        message={
          pendingQualify?.kind === 'qualified'
            ? 'Are you sure you want to mark this prelead as Qualified?'
            : 'Are you sure you want to mark this prelead as Unqualified?'
        }
      />
      {selectedPreleadForAnswers && (
        <WhatsAppFlowAnswersModal
          selectedPrelead={selectedPreleadForAnswers}
          onClose={() => setSelectedPreleadForAnswers(null)}
        />
      )}
      {selectedPreleadForLogs && (
        <PreleadLogModal
          prelead={selectedPreleadForLogs}
          onClose={() => setSelectedPreleadForLogs(null)}
          onLogAdded={refreshCurrentListing}
        />
      )}
    </Layout>
  );
};

export default AllPreleadLeadsPage;
